import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { firstIdOf, isFieldVisible, type DocumentDef } from '@project/contracts'
import { Button, Modal } from '../design/index.js'
import { useApi } from '../app/api.js'
import { navigate, paths, type Query } from '../app/router.js'
import { FormField } from './controls.js'
import { useSettings } from './queries.js'
import { ErrorBlock, Loading } from './feedback.js'

export interface CreateWizardProps {
  readonly def: DocumentDef
  readonly companyId: string
  readonly docRoute: string
  /** Values carried in from the URL, e.g. the parent document on Related Docs. */
  readonly seed: Query
  readonly onClose: () => void
}

/**
 * Creation always starts by capturing the few answers that decide what the form
 * looks like. The wizard is the document definition's `createWizard`, nothing
 * more — there is no per-document creation screen anywhere in this client.
 */
export const CreateWizard = ({ def, companyId, docRoute, seed, onClose }: CreateWizardProps) => {
  const api = useApi()
  const wizard = def.createWizard
  const settings = useSettings()

  /*
    What the document this was raised from already answers.

    "Create the next document" used to carry the source id in the URL and
    nothing on the server read it, so approving a purchase order and pressing
    "Receipt note (GRN)" opened an empty wizard in which the supplier and the
    order had to be found and picked again by hand. The server now says what
    the new document should start with — including the back-reference that puts
    its lines into the allocation ledger — and the wizard opens on that.
  */
  const from = seed['from']
  const fromType = seed['fromType']
  const carried = useQuery({
    queryKey: ['createSeed', api.companyId, def.key, from, fromType],
    queryFn: () => api.createSeed(def.key, from!, fromType!),
    enabled: Boolean(from && fromType),
    staleTime: 60_000,
  })

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {}
    for (const field of wizard.fields) {
      const seeded = seed[field.key]
      if (seeded !== undefined) initial[field.key] = seeded
      else if (field.defaultValue !== undefined) initial[field.key] = field.defaultValue
    }
    return initial
  })
  const [touched, setTouched] = useState(false)
  /** Fields the operator has set or cleared by hand; a default never overrides them. */
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set(Object.keys(seed)))

  /*
    What the company's defaults answer (INV-024): the default tax unit, the
    person raising it, and whatever a document already named carries — an
    invoice against an order starts under that order's tax unit. Asked again
    as the parties and documents named change, and never over an answer.
  */
  const lookupAnswers: Record<string, string> = {}
  for (const field of wizard.fields) {
    // A multi-select answers with the first of its records (BILL-005).
    const v = field.control === 'lookup' ? firstIdOf(values[field.key]) : null
    if (v !== null && field.lookup?.entity !== 'tax_unit') lookupAnswers[field.key] = v
  }
  const defaults = useQuery({
    queryKey: ['createDefaults', api.companyId, def.key, JSON.stringify(lookupAnswers)],
    queryFn: () => api.createDefaults(def.key, lookupAnswers),
    enabled: !(from && fromType) || carried.isSuccess || carried.isError,
    staleTime: 60_000,
    retry: false,
  })
  const [defaultLabels, setDefaultLabels] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!defaults.data) return
    setDefaultLabels((prev) => ({ ...prev, ...defaults.data.labels }))
    setValues((prev) => {
      const next = { ...prev }
      for (const [key, value] of Object.entries(defaults.data.values)) {
        if (!handled.has(key) && (next[key] === undefined || next[key] === '')) next[key] = value
      }
      return next
    })
  }, [defaults.data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Merged rather than replaced: anything the URL said explicitly stays put,
  // and the operator's own edits survive the answer arriving.
  useEffect(() => {
    if (!carried.data) return
    setValues((prev) => {
      const next = { ...prev }
      for (const [key, value] of Object.entries(carried.data.values)) {
        if (next[key] === undefined || next[key] === '') next[key] = value
      }
      return next
    })
  }, [carried.data])

  // A question the definition does not ask — of this order type, or of a
  // company with the feature switched off — is neither shown nor required.
  const asked = wizard.fields.filter((field) => isFieldVisible(field, values, settings.data?.values))
  const missing = asked.filter((f) => f.required && !String(values[f.key] ?? '').trim()).map((f) => f.key)

  const create = useMutation({
    mutationFn: () => {
      const fields: Record<string, unknown> = { ...(carried.data?.values ?? {}), ...values }
      if (from) fields['fromDocumentId'] = from
      if (fromType) fields['fromDocumentType'] = fromType
      if (seed['clone']) fields['cloneOfDocumentId'] = seed['clone']
      return api.create(def.key, fields)
    },
    onSuccess: (doc) => navigate(paths.document(companyId, docRoute, doc.id)),
  })

  /*
    "You can also copy another request" (MR-004, PUR-035): the order to copy
    is picked here, in the create dialog, rather than found and opened first.
    The copy is the server's — header and lines, re-checked where the type
    says so — and the new draft is what opens.
  */
  const canCopy = def.actions.includes('clone') && !(from && fromType)
  const [copying, setCopying] = useState(false)
  const [copyFrom, setCopyFrom] = useState('')
  const copy = useMutation({
    mutationFn: () => api.clone(def.key, copyFrom),
    onSuccess: (doc) => navigate(paths.document(companyId, docRoute, doc.id)),
  })
  const copyField = {
    key: 'copyFrom', label: `${def.label} to copy`, control: 'lookup' as const, required: true,
    lookup: { entity: def.key }, width: 'full' as const,
  }

  const waiting = carried.isLoading

  /*
    A type with nothing to ask before its form opens the form itself — the
    reference's "Create Tool Request" (STK-014). The draft is made at once:
    the server's create defaults fill what they always fill (the date, the
    person raising it), and the rest is asked on the form.
  */
  const direct = wizard.fields.length === 0
  useEffect(() => {
    if (direct && !waiting && create.isIdle) create.mutate()
  }, [direct, waiting]) // eslint-disable-line react-hooks/exhaustive-deps
  if (direct) {
    return (
      <Modal
        title={wizard.title}
        onClose={onClose}
        actions={<Button kind="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>}
      >
        {create.error ? <ErrorBlock error={create.error} /> : <Loading />}
      </Modal>
    )
  }

  if (copying) {
    return (
      <Modal
        title={`Copy ${def.label.toLowerCase()}`}
        subtitle={`Pick the ${def.label.toLowerCase()} to start from. A new draft is made from it.`}
        onClose={onClose}
        actions={
          <>
            <Button kind="ghost" onClick={() => setCopying(false)} disabled={copy.isPending}>Back</Button>
            <Button kind="primary" disabled={copy.isPending || copyFrom === ''} onClick={() => copy.mutate()}>
              {copy.isPending ? 'Copying…' : 'Done'}
            </Button>
          </>
        }
      >
        {copy.error ? <ErrorBlock error={copy.error} /> : null}
        <FormField field={copyField} value={copyFrom} onChange={(v) => setCopyFrom(typeof v === 'string' ? v : '')} />
      </Modal>
    )
  }

  return (
    <Modal
      title={wizard.title}
      subtitle={wizard.subtitle}
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button
            kind="primary"
            disabled={create.isPending || waiting}
            onClick={() => {
              setTouched(true)
              if (missing.length === 0) create.mutate()
            }}
          >
            {create.isPending ? 'Creating…' : 'Done'}
          </Button>
        </>
      }
    >
      {create.error ? <ErrorBlock error={create.error} /> : null}

      {waiting ? <Loading /> : (
        <div className="stack" style={{ marginTop: create.error ? 20 : 0 }}>
          {asked.map((field) => (
            <FormField
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              valueLabel={carried.data?.labels[field.key] ?? defaultLabels[field.key]}
              scopeValues={values}
              onChange={(v) => {
                setHandled((prev) => new Set([...prev, field.key]))
                setValues((prev) => ({ ...prev, [field.key]: v }))
              }}
              error={touched && missing.includes(field.key) ? `${field.label} is required.` : undefined}
            />
          ))}
          {canCopy ? (
            <p className="helper">
              <button type="button" className="link-button" onClick={() => setCopying(true)}>
                You can also copy another {def.label.toLowerCase()}.
              </button>
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  )
}
